/**
 * QuantityInputModal - Reusable modal for entering activity quantity
 * Used by both desktop QuickLogPanel and mobile QuickLogModal
 */

import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Check, ChevronDown } from 'lucide-react';
import type { ActivityType } from '@lib/types';
import { useActiveMessages } from '@hooks/useMessages';

interface QuantityInputModalProps {
  isOpen: boolean;
  activityType: ActivityType | null;
  onConfirm: (quantity: number, linkedinMessageId?: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const QUICK_PRESETS = [1, 5, 10, 25, 50];

export function QuantityInputModal({
  isOpen,
  activityType,
  onConfirm,
  onCancel,
  isLoading = false,
}: QuantityInputModalProps): JSX.Element {
  const [quantity, setQuantity] = useState('1');
  const [error, setError] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { data: activeMessages = [] } = useActiveMessages();
  const isLinkedInSent = activityType?.id === 'linkedin_sent';

  useEffect(() => {
    if (isOpen) {
      setQuantity('1');
      setError('');
      setSelectedMessageId('');
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen || !activityType) return <></>;

  const numericQuantity = parseInt(quantity, 10) || 0;
  const isValid = numericQuantity >= 1 && numericQuantity <= 100;
  const estimatedPoints = isValid ? numericQuantity * activityType.points_per_unit : 0;

  const handleInputChange = (value: string): void => {
    // Only allow numeric input
    const cleaned = value.replace(/[^0-9]/g, '');
    setQuantity(cleaned);
    setError('');

    const num = parseInt(cleaned, 10);
    if (cleaned && (num < 1 || num > 100)) {
      setError('Quantity must be between 1 and 100');
    }
  };

  const handlePresetClick = (preset: number): void => {
    setQuantity(preset.toString());
    setError('');
  };

  const handleSubmit = (): void => {
    if (!isValid) {
      setError('Please enter a valid quantity (1-100)');
      return;
    }
    onConfirm(numericQuantity, selectedMessageId || undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && isValid && !isLoading) {
      handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quantity-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm z-[100]"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative z-[101] bg-ybh-bg-secondary border border-white/[0.04] rounded-2xl w-full max-w-sm max-h-[85vh] overflow-hidden animate-scale-in flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-transparent">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${activityType.color}20`, color: activityType.color }}
            >
              <Check size={20} />
            </div>
            <div>
              <h2 id="quantity-modal-title" className="font-display text-lg text-ybh-text-primary tracking-wide">
                {activityType.name.toUpperCase()}
              </h2>
              <p className="text-xs text-ybh-text-muted">+{activityType.points_per_unit} pts each</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-lg bg-ybh-bg-tertiary hover:bg-ybh-bg-elevated flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={18} className="text-ybh-text-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          {/* Quantity input */}
          <div className="space-y-2">
            <label htmlFor="quantity-input" className="block text-xs font-medium text-ybh-text-muted tracking-widest uppercase">
              How many?
            </label>
            <input
              ref={inputRef}
              id="quantity-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={quantity}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className={`
                w-full px-4 py-4 rounded-lg bg-ybh-bg-tertiary border-2
                text-ybh-text-primary text-center font-display text-4xl tracking-wide
                placeholder:text-ybh-text-muted focus:outline-none transition-colors
                ${error ? 'border-ybh-error' : 'border-transparent focus:border-ybh-orange'}
                disabled:opacity-50
              `}
              placeholder="0"
            />
            {error && (
              <p className="text-xs text-ybh-error">{error}</p>
            )}
          </div>

          {/* LinkedIn message selector - only for LinkedIn Sent */}
          {isLinkedInSent && activeMessages.length > 0 && (
            <div className="space-y-2">
              <label htmlFor="message-select" className="block text-xs font-medium text-ybh-text-muted tracking-widest uppercase">
                Which message did you use?
              </label>
              <div className="relative">
                <select
                  id="message-select"
                  value={selectedMessageId}
                  onChange={(e) => setSelectedMessageId(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-3 pr-10 rounded-lg bg-ybh-bg-tertiary border-2 border-transparent text-ybh-text-primary text-sm focus:outline-none focus:border-ybh-orange transition-colors appearance-none disabled:opacity-50"
                >
                  <option value="">None / Other</option>
                  {activeMessages.map((msg) => (
                    <option key={msg.id} value={msg.id}>
                      {msg.title}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-ybh-text-muted pointer-events-none" />
              </div>
            </div>
          )}

          {/* Quick presets */}
          <div className="space-y-2">
            <p className="text-xs text-ybh-text-muted tracking-widest uppercase">Quick select</p>
            <div className="grid grid-cols-5 gap-2">
              {QUICK_PRESETS.map((preset) => (
                <button
                  key={preset}
                  onClick={() => handlePresetClick(preset)}
                  disabled={isLoading}
                  className={`
                    py-3 rounded-lg font-semibold text-sm transition-all
                    ${quantity === preset.toString()
                      ? 'bg-ybh-orange text-ybh-text-primary'
                      : 'bg-ybh-bg-tertiary text-ybh-text-secondary hover:bg-ybh-bg-elevated hover:text-ybh-text-primary'
                    }
                    disabled:opacity-50
                  `}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          {/* Points preview */}
          {isValid && (
            <div className="p-4 rounded-lg bg-ybh-bg-tertiary border border-transparent">
              <div className="flex items-center justify-between">
                <span className="text-sm text-ybh-text-muted">
                  {numericQuantity} x {activityType.points_per_unit} pts
                </span>
                <span
                  className="font-display text-2xl tracking-wide"
                  style={{ color: activityType.color }}
                >
                  +{estimatedPoints}
                </span>
              </div>
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
            className="w-full py-4 rounded-lg font-semibold text-ybh-text-primary bg-ybh-gradient hover:shadow-glow-orange transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Logging...</span>
              </>
            ) : (
              <span>Log {numericQuantity > 1 ? `${numericQuantity} ` : ''}{activityType.name}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
